import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ChatsList.module.css";

function getCurrentUserId() {
  try {
    const rawProfile = localStorage.getItem("userProfile");
    if (!rawProfile) return "";

    const profile = JSON.parse(rawProfile);

    return String(
      profile?.id ||
        profile?.userId ||
        profile?.employeeId ||
        profile?.uid ||
        profile?.guid ||
        profile?.uuid ||
        ""
    );
  } catch (error) {
    console.error("Ошибка чтения userProfile:", error);
    return "";
  }
}

function ChatsList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [unreadByEventId, setUnreadByEventId] = useState({});
  const [lastMessageAtByEventId, setLastMessageAtByEventId] = useState({});

  const chats = useMemo(() => {
    try {
      const raw = localStorage.getItem("eventsData");
      if (!raw) return [];

      const parsed = JSON.parse(raw);

      const allEvents = [
        ...(parsed?.current || parsed?.grouped?.current || []),
        ...(parsed?.future || parsed?.grouped?.future || []),
        ...(parsed?.past || parsed?.grouped?.past || []),
      ];

      const map = new Map();

      allEvents.forEach((event) => {
        if (!event?.id) return;
        if (!map.has(String(event.id))) {
          map.set(String(event.id), event);
        }
      });

      return Array.from(map.values());
    } catch (error) {
      console.error("Ошибка чтения eventsData для списка чатов:", error);
      return [];
    }
  }, []);

  const loadUnreadCounts = async () => {
    try {
      const userId = getCurrentUserId();

      if (!userId) {
        setUnreadByEventId({});
        return;
      }

      const response = await fetch(
        `/api/events/chats/unread?userId=${encodeURIComponent(userId)}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (response.status === 401) {
        navigate("/");
        return;
      }

      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }

      const data = await response.json();
      setUnreadByEventId(data?.byEventId || {});
      setLastMessageAtByEventId(data?.lastMessageAtByEventId || {});
    } catch (error) {
      console.error("Ошибка загрузки непрочитанных сообщений:", error);
    }
  };

  useEffect(() => {
    let mounted = true;

    const safeLoadUnreadCounts = async () => {
      if (!mounted) return;
      await loadUnreadCounts();
    };

    safeLoadUnreadCounts();

    const intervalId = window.setInterval(safeLoadUnreadCounts, 30000);

    const handleFocus = () => {
      safeLoadUnreadCounts();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const filteredChats = useMemo(() => {
  const q = searchTerm.trim().toLowerCase();

  const filtered = q
    ? chats.filter((event) => {
        const name = event?.name?.toLowerCase() || "";
        const id = String(event?.id || "").toLowerCase();
        const location = event?.location?.toLowerCase() || "";

        return name.includes(q) || id.includes(q) || location.includes(q);
      })
    : chats;

  return [...filtered].sort((a, b) => {
    const timeA = new Date(
      lastMessageAtByEventId[String(a.id)] || 0
    ).getTime();

    const timeB = new Date(
      lastMessageAtByEventId[String(b.id)] || 0
    ).getTime();

    return timeB - timeA;
  });
}, [chats, searchTerm, lastMessageAtByEventId]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("ru-RU");
  };

  const openChat = (eventId) => {
    navigate(`/events/${eventId}?openChat=1`);
  };

  return (
    <div className={styles.chatsContainer}>
      <div className={styles.searchPanel}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Поиск.."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className={styles.clearSearch}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className={styles.chatsList}>
        {filteredChats.length > 0 ? (
          filteredChats.map((event) => {
            const unreadCount = unreadByEventId[String(event.id)] || 0;

            return (
              <button
                key={event.id}
                type="button"
                className={`${styles.chatCard} ${
                  unreadCount > 0 ? styles.chatCardUnread : ""
                }`}
                onClick={() => openChat(event.id)}
              >
                <div className={styles.chatCardTop}>
                  <div className={styles.chatNameWrap}>
                    <div className={styles.chatName}>{event.name}</div>

                    {unreadCount > 0 && (
                      <span className={styles.unreadBadge}>
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </div>

                  <div className={styles.chatProgram}>№ {event.id}</div>
                </div>

                <div className={styles.chatMeta}>
                  <span>
                    <strong>Дата:</strong> {formatDate(event.dateBegin)}
                    {event.dateEnd ? ` — ${formatDate(event.dateEnd)}` : ""}
                  </span>
                </div>
              </button>
            );
          })
        ) : (
          <div className={styles.emptyState}>
            {searchTerm ? "Ничего не найдено" : "Пока нет доступных чатов"}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatsList;
